import { Button, Field, Typography } from '@/components';
import { t } from 'i18next';
import { Dispatch, forwardRef, SetStateAction, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Slot } from '../modals/action-offer-modals';

interface AvailabilitySlotsPickerProps {
  onSlotsChange?: (slots: Slot[]) => void;
  slots: Slot[];
  setSlots: Dispatch<SetStateAction<Slot[]>>;
}

type CustomInputProps = {
  value?: string;
  onClick?: () => void;
  placeholder?: string;
  className?: string;
};

export function AvailabilitySlotsPicker({ onSlotsChange, slots, setSlots }: AvailabilitySlotsPickerProps) {
  const [tempSlot, setTempSlot] = useState<Slot>({ id: 0, date: null, from: '', to: '' });
  const [errors, setErrors] = useState<string>('');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const MIN_SLOTS_LENGTH = 3;

  const handleChange = (field: keyof Slot, value: any) => {
    setTempSlot({ ...tempSlot, [field]: value });
    setIsCalendarOpen(false);
  };

  const addSlot = () => {
    if (!tempSlot.date || !tempSlot.from || !tempSlot.to) {
      setErrors('All fields are mandatory');
      return;
    }
    if (tempSlot.to <= tempSlot.from) {
      setErrors('The end time must be later than the start time');
      return;
    }
    const newSlot = { ...tempSlot, id: Date.now() };
    const newSlots = [...slots, newSlot];
    setSlots(newSlots);
    onSlotsChange?.(newSlots);
    setTempSlot({ id: 0, date: null, from: '', to: '' });
    setErrors('');
  };

  const removeSlot = (id: number) => {
    const newSlots = slots.filter((slot) => slot.id !== id);
    setSlots(newSlots);
    onSlotsChange?.(newSlots);
  };

  const generateTimeOptions = (startHour: number = 9, endHour: number = 18, fromTime: string = '') => {
    const options = [];
    const start = new Date();
    start.setHours(startHour, 0, 0, 0);
    const end = new Date();
    end.setHours(endHour, 45, 0, 0);

    for (let d = new Date(start); d <= end; d.setMinutes(d.getMinutes() + 60)) {
      const hours = d.getHours().toString().padStart(2, '0');
      const minutes = d.getMinutes().toString().padStart(2, '0');
      const value = `${hours}:${minutes}`;

      if (!fromTime || value > fromTime) {
        options.push(
          <option key={value} value={value}>
            {value}
          </option>
        );
      }
    }
    return options;
  };

  useEffect(() => {
    if (tempSlot.date && tempSlot.from && tempSlot.to) {
      setErrors('');
    }
    if (tempSlot.to <= tempSlot.from) {
      setErrors('');
    }
  }, [tempSlot.date, tempSlot.from, tempSlot.to]);

  const ReadOnlyInput = forwardRef<HTMLInputElement, CustomInputProps>(({ value, onClick, placeholder }, ref) => (
    <input
      readOnly
      ref={ref}
      value={value}
      onClick={onClick}
      placeholder={placeholder}
      className="focus:border-primary-700 focus:ring-primary-700 w-full rounded-xl border border-gray-200 px-3 py-2 text-base focus:ring-1 focus:outline-hidden"
    />
  ));

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-6 md:flex-row">
        <div className="space-y-4 md:w-1/2">
          <Field id="date" label={t('Date')} classes={{ field: 'space-y-1' }}>
            <DatePicker
              wrapperClassName="w-full"
              selected={tempSlot.date}
              onChange={(date: any) => handleChange('date', date)}
              minDate={new Date()}
              placeholderText="Pick a date"
              dateFormat="dd-MM-yyyy"
              filterDate={(date) => {
                const day = date.getDay();
                return day !== 0 && day !== 6;
              }}
              maxDate={new Date(new Date().setMonth(new Date().getMonth() + 1))}
              onInputClick={() => setIsCalendarOpen(true)}
              onClickOutside={() => setIsCalendarOpen(false)}
              open={isCalendarOpen}
              popperClassName="z-50"
              dayClassName={(date) => {
                const isSelected = tempSlot.date?.toDateString() === date.toDateString();
                const isToday = new Date().toDateString() === date.toDateString();
                const isWeekend = [0, 6].includes(date.getDay());
                return [
                  'w-9 h-9 flex items-center justify-center rounded-full transition-all duration-150',
                  isSelected ? '!bg-primary-700 !text-white' : '',
                  isToday && !isSelected ? 'bg-primary-100 text-black' : '',
                  isWeekend ? 'text-gray-400' : 'text-black hover:bg-primary-100 hover:text-primary-700',
                ].join(' ');
              }}
              customInput={<ReadOnlyInput />}
            />
          </Field>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field id="from-time" label={t('Start time')} classes={{ field: 'space-y-1' }}>
              <select
                value={tempSlot.from}
                onChange={(e) => handleChange('from', e.target.value)}
                className="focus:border-primary-700 focus:ring-primary-700 w-full rounded-xl border border-gray-200 px-3 py-2 text-base focus:ring-1 focus:outline-hidden"
              >
                <option value="">--:--</option>
                {generateTimeOptions()}
              </select>
            </Field>

            <Field id="to-time" label={t('End time')} classes={{ field: 'space-y-1' }}>
              <select
                value={tempSlot.to}
                onChange={(e) => handleChange('to', e.target.value)}
                className="focus:border-primary-700 focus:ring-primary-700 w-full rounded-xl border border-gray-200 px-3 py-2 text-base focus:ring-1 focus:outline-hidden"
              >
                <option value="">--:--</option>
                {generateTimeOptions(9, 18, tempSlot.from)}
              </select>
            </Field>
          </div>

          {errors && (
            <Typography as="p" weight="normal" color="error">
              {errors}
            </Typography>
          )}

          <Button type="button" color="primary" onClick={addSlot} className="w-full">
            + Add slot
          </Button>
        </div>

        <div className="max-h-80 w-full space-y-2 overflow-y-auto md:w-1/2">
          <Typography as="p" weight="medium">
            Your slots:
          </Typography>
          {slots.map((slot) => (
            <div key={slot.id} className="flex items-center justify-around rounded-xl border p-2 shadow-xs">
              <Typography as="span" weight="light" size="body-2">
                {slot.date?.toLocaleDateString('es-ES')} from {slot.from} to {slot.to}
              </Typography>
              <Button
                type="button"
                color="secondary"
                icon="trash"
                className="p-1"
                onClick={() => removeSlot(slot.id)}
              />
            </div>
          ))}
          {slots.length < MIN_SLOTS_LENGTH && (
            <Typography as="p" size="body-3" color="secondary" weight="normal" className="text-start">
              {t('Add at least 3 time slots')}
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
}
